import { createFileRoute, useNavigate, useRouter, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Star, Calendar, Globe, Users, Clapperboard } from "lucide-react";
import { getStoryBySlug, STORIES, type StoryEntry } from "@/lib/stories-data";
import { useTmdbMedia, tmdbImage } from "@/lib/use-tmdb-media";

export const Route = createFileRoute("/story/$slug")({
  head: ({ params }) => {
    const s = getStoryBySlug(params.slug);
    const title = s ? `${s.title} — قصة | Rawan UI` : "قصة غير موجودة | Rawan UI";
    const desc = s?.overview ?? "قصة ويب من Rawan UI.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
      ],
    };
  },
  notFoundComponent: NotFound,
  component: StoryPage,
});

function NotFound() {
  return (
    <div className="min-h-dvh grid place-items-center bg-background text-foreground">
      <div className="text-center space-y-3">
        <h1 className="text-2xl font-bold">القصة غير موجودة</h1>
        <Link to="/" className="text-primary underline">العودة للرئيسية</Link>
      </div>
    </div>
  );
}

const PAGE_MS = 6000;

function StoryPage() {
  const { slug } = Route.useParams();
  const story = getStoryBySlug(slug);
  const navigate = useNavigate();
  const router = useRouter();

  if (!story) return <NotFound />;

  const media = useTmdbMedia(story.tmdbType, story.tmdbId);
  const backdrop = story.backdropUrl
    ? story.backdropUrl
    : media.backdrop_path
    ? tmdbImage(media.backdrop_path, "w780")
    : `https://picsum.photos/seed/${encodeURIComponent(story.slug)}-bd/1280/720`;
  const poster = story.posterUrl
    ? story.posterUrl
    : media.poster_path
    ? tmdbImage(media.poster_path, "w500")
    : `https://picsum.photos/seed/${encodeURIComponent(story.slug)}-ps/500/750`;

  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const startedAt = useRef<number>(Date.now());
  const elapsed = useRef<number>(0);
  const [, forceTick] = useState(0);

  const close = useCallback(() => {
    if (window.history.length > 1) router.history.back();
    else navigate({ to: "/" });
  }, [navigate, router]);

  // Advance pages
  useEffect(() => {
    startedAt.current = Date.now();
    elapsed.current = 0;
  }, [page]);

  useEffect(() => {
    if (paused) {
      // freeze elapsed
      elapsed.current += Date.now() - startedAt.current;
      return;
    }
    startedAt.current = Date.now();
    const raf = { id: 0 as number };
    const tick = () => {
      const total = elapsed.current + (Date.now() - startedAt.current);
      if (total >= PAGE_MS) {
        if (page < 2) {
          setPage((p) => p + 1);
        } else {
          close();
        }
        return;
      }
      forceTick((n) => (n + 1) % 1000);
      raf.id = window.requestAnimationFrame(tick);
    };
    raf.id = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf.id);
  }, [paused, page, close]);

  // ESC closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const progresses = useMemo(() => {
    const now = paused ? elapsed.current : elapsed.current + (Date.now() - startedAt.current);
    return [0, 1, 2].map((i) => {
      if (i < page) return 100;
      if (i > page) return 0;
      return Math.min(100, (now / PAGE_MS) * 100);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, paused, /* tick */ Math.floor((Date.now() / 50))]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black text-white flex items-center justify-center select-none"
      role="dialog"
      aria-modal="true"
      aria-label={story.title}
      onPointerDown={() => setPaused(true)}
      onPointerUp={() => setPaused(false)}
      onPointerLeave={() => setPaused(false)}
    >
      {/* Backdrop blurred */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30 blur-2xl scale-110"
        style={{ backgroundImage: `url(${backdrop})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/80" aria-hidden />

      {/* Phone-like frame */}
      <div className="relative w-full h-full sm:h-[92vh] sm:max-h-[860px] sm:w-[460px] sm:rounded-2xl overflow-hidden shadow-2xl bg-neutral-950">
        {/* Progress bars */}
        <div className="absolute top-2 left-2 right-2 z-30 flex gap-1">
          {progresses.map((p, i) => (
            <div key={i} className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
              <div className="h-full bg-white" style={{ width: `${p}%`, transition: paused ? "none" : "width 60ms linear" }} />
            </div>
          ))}
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute top-5 end-3 z-40 h-9 w-9 grid place-items-center rounded-full bg-black/50 hover:bg-black/70 backdrop-blur"
          aria-label="إغلاق"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Pages */}
        {page === 0 && <PageHero story={story} backdrop={backdrop} poster={poster} />}
        {page === 1 && <PageOverview story={story} backdrop={backdrop} />}
        {page === 2 && <PageCast story={story} poster={poster} />}
      </div>
    </div>
  );
}

function PageHero({ story, backdrop, poster }: { story: StoryEntry; backdrop: string; poster: string }) {
  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        className="flex-1 bg-cover bg-center"
        style={{ backgroundImage: `url(${backdrop})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" aria-hidden />
      <div className="relative mt-auto p-5 pb-10 space-y-4">
        <div className="flex items-end gap-4">
          <img
            src={poster}
            alt={story.title}
            className="w-24 h-36 rounded-lg object-cover shadow-xl border border-white/10"
          />
          <div className="flex-1 min-w-0">
            <span className="inline-block text-[10px] tracking-wider uppercase bg-primary/90 px-2 py-0.5 rounded-full mb-2">
              {story.tmdbType === "tv" ? "مسلسل" : "فيلم"}
            </span>
            <h1 className="text-2xl font-extrabold leading-tight">{story.title}</h1>
            <p className="text-xs text-white/70 mt-1">{story.titleEn}</p>
            <div className="mt-2 flex items-center gap-3 text-xs text-white/85">
              <span className="inline-flex items-center gap-1">
                <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                {story.rating.toFixed(1)}
              </span>
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {story.year}
              </span>
              <span className="inline-flex items-center gap-1">
                <Globe className="h-3.5 w-3.5" />
                {story.country}
              </span>
            </div>
          </div>
        </div>
        <p className="text-sm text-white/90 italic">«{story.highlight}»</p>
      </div>
    </div>
  );
}

function PageOverview({ story, backdrop }: { story: StoryEntry; backdrop: string }) {
  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        className="h-1/2 bg-cover bg-center"
        style={{ backgroundImage: `url(${backdrop})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/50 to-black" aria-hidden />
      <div className="relative mt-auto p-5 pb-10 space-y-4">
        <div className="flex items-center gap-2 text-xs text-white/80">
          <Clapperboard className="h-4 w-4" />
          <span>القصة</span>
        </div>
        <h2 className="text-xl font-bold">{story.title}</h2>
        <div className="flex flex-wrap gap-2">
          {story.genres.map((g) => (
            <span key={g} className="text-[11px] px-2 py-1 rounded-full bg-white/10 border border-white/15">
              {g}
            </span>
          ))}
        </div>
        <p className="text-sm leading-relaxed text-white/90">{story.overview}</p>
      </div>
    </div>
  );
}

function PageCast({ story, poster }: { story: StoryEntry; poster: string }) {
  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        className="h-2/5 bg-cover bg-center"
        style={{ backgroundImage: `url(${poster})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/60 to-black" aria-hidden />
      <div className="relative mt-auto p-5 pb-10 space-y-5">
        <div className="flex items-center gap-2 text-xs text-white/80">
          <Users className="h-4 w-4" />
          <span>طاقم العمل</span>
        </div>
        <h2 className="text-xl font-bold">{story.title}</h2>
        <ul className="space-y-2">
          {story.cast.map((name) => (
            <li
              key={name}
              className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2"
            >
              <span className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-amber-500 grid place-items-center text-xs font-bold">
                {name.trim().charAt(0)}
              </span>
              <span className="text-sm">{name}</span>
            </li>
          ))}
        </ul>
        <div className="pt-2 text-center">
          <Link
            to={story.tmdbType === "tv" ? "/series/$slug" : "/movie/$slug"}
            params={{ slug: story.slug }}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 transition"
            onPointerDown={(e) => e.stopPropagation()}
          >
            مشاهدة التفاصيل
          </Link>
        </div>
      </div>
    </div>
  );
}

// Surface STORIES so build-time tree-shaking keeps the list for hot reload.
void STORIES;
